import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';

export default function SubscriptionReturn() {
    // PayPal redirects here after approval: ?subscription_id=...
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user, refreshUser, refreshChildren } = useAuth();
    const [state, setState] = useState('activating');
    const [err, setErr] = useState(null);

    React.useEffect(() => {
        const ppid = params.get('subscription_id');
        if (!ppid || !user) return;
        let cancelled = false;
        (async () => {
            try {
                await api.post('/subscription/paypal/activate', { subscription_id: ppid });
                // Belt-and-braces: also pull live state from PayPal so the entitlement
                // lands correctly even if the upstream activate didn't know the tier.
                try { await api.post('/me/subscription/sync'); } catch { /* non-fatal */ }
                // Refresh local state so cached subscription_status is no longer 'none'.
                const [, freshChildren] = await Promise.all([refreshUser?.(), refreshChildren?.()]);
                if (cancelled) return;
                setState('activated');
                toast.success('Subscription activated. Welcome to Linguistic Quest!', {
                    duration: 3000,
                });
                // Auto-redirect: if no children yet, go to add-child onboarding;
                // otherwise drop them on the dashboard (avoid sending users to
                // add-child only to bounce off the tier cap).
                const hasChildren = (freshChildren || []).length > 0;
                setTimeout(() => {
                    if (cancelled) return;
                    navigate(hasChildren ? '/dashboard' : '/onboarding/add-child', { replace: true });
                }, 1500);
            } catch (e) {
                if (cancelled) return;
                setErr(e?.response?.data?.detail || 'Activation failed');
                setState('error');
            }
        })();
        return () => { cancelled = true; };
    }, [params, user, refreshUser, refreshChildren, navigate]);

    return (
        <main className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="page-subscription-return">
            {state === 'activating' && <p className="text-lq-ink-500" data-testid="sub-return-activating">Activating your subscription…</p>}
            {state === 'activated' && (
                <div data-testid="sub-return-activated">
                    <CheckCircle2 size={48} className="mx-auto text-lq-success" />
                    <h1 className="mt-4 font-display text-4xl font-semibold text-lq-ink-black">You are in.</h1>
                    <p className="mt-3 text-lq-ink-700">Your 7-day free trial is active. Taking you to add a child profile…</p>
                    <Link to="/onboarding/add-child" className="mt-6 inline-flex bg-lq-purple-500 text-white px-6 py-3 rounded-full font-semibold" data-testid="sub-return-add-child-link">
                        Add a child now
                    </Link>
                </div>
            )}
            {state === 'error' && <p className="text-lq-error" data-testid="sub-return-error">{err}</p>}
        </main>
    );
}
